import { useEffect, useState, useCallback } from "react";
import { LogOut, RefreshCw, CalendarCheck, Mail, Briefcase, Handshake } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HOTEL } from "@/data/hotel";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const TABS = [
  { key: "bookings", label: "Bookings", icon: CalendarCheck, path: "/api/admin/bookings" },
  { key: "contacts", label: "Contacts", icon: Mail, path: "/api/admin/contacts" },
  { key: "careers", label: "Careers", icon: Briefcase, path: "/api/admin/careers" },
  { key: "partnerships", label: "Partnerships", icon: Handshake, path: "/api/admin/partnerships" },
];

const COLUMNS = {
  bookings: [
    ["created_at", "Received", fmtDate],
    ["full_name", "Guest"],
    ["email", "Email"],
    ["phone", "Phone"],
    ["room_name", "Room"],
    ["check_in", "Check-in"],
    ["check_out", "Check-out"],
    ["guests", "Guests"],
    ["special_requests", "Requests"],
  ],
  contacts: [
    ["created_at", "Received", fmtDate],
    ["full_name", "Name"],
    ["email", "Email"],
    ["subject", "Subject"],
    ["message", "Message"],
  ],
  careers: [
    ["created_at", "Received", fmtDate],
    ["full_name", "Name"],
    ["email", "Email"],
    ["phone", "Phone"],
    ["role_of_interest", "Role"],
    ["message", "Message"],
  ],
  partnerships: [
    ["created_at", "Received", fmtDate],
    ["full_name", "Owner"],
    ["email", "Email"],
    ["phone", "Phone"],
    ["property_address", "Property"],
    ["number_of_rooms", "Rooms"],
    ["google_pin", "Google PIN"],
    ["description", "Description"],
  ],
};

const DataTable = ({ tabKey, rows }) => {
  const cols = COLUMNS[tabKey];
  if (!rows.length) {
    return (
      <div className="text-center py-16 text-[#8F7858]" data-testid={`admin-empty-${tabKey}`}>
        No {tabKey} yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-[#e4d9c6] bg-white">
      <table className="w-full text-sm" data-testid={`admin-table-${tabKey}`}>
        <thead>
          <tr className="bg-[#f6efe0] text-left">
            {cols.map(([, label]) => (
              <th
                key={label}
                className="px-4 py-3 text-[11px] uppercase tracking-wider text-[#8F7858] font-semibold whitespace-nowrap"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} className="border-t border-[#efe6d5] align-top hover:bg-[#faf6ec]">
              {cols.map(([field, label, fmt]) => (
                <td key={label} className="px-4 py-3 text-[#2b2b2b] max-w-[260px]">
                  <div className="whitespace-pre-wrap break-words">
                    {fmt ? fmt(row[field]) : (row[field] ?? "—") === "" ? "—" : row[field] ?? "—"}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div
    className="bg-white border border-[#e4d9c6] rounded-2xl px-5 py-4 flex items-center gap-4"
    data-testid={`admin-stat-${label.toLowerCase()}`}
  >
    <div className="w-11 h-11 rounded-xl bg-[#f4ead6] flex items-center justify-center text-[#A48352]">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-2xl font-serif text-[#1E1A14] leading-none">{value ?? "—"}</div>
      <div className="text-xs uppercase tracking-wider text-[#8F7858] mt-1">{label}</div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { user, logout, authFetch } = useAuth();
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ bookings: [], contacts: [], careers: [], partnerships: [] });
  const [active, setActive] = useState("bookings");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, ...lists] = await Promise.all([
        authFetch("/api/admin/stats"),
        ...TABS.map((t) => authFetch(t.path)),
      ]);
      setStats(statsRes);
      const next = {};
      TABS.forEach((t, i) => {
        next[t.key] = lists[i];
      });
      setData(next);
    } catch {
      /* session handled in authFetch */
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen" style={{ background: "#FAF4E6" }} data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#e4d9c6] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={HOTEL.logo} alt="Azulo" className="h-8 object-contain" />
            <span className="hidden sm:block text-sm text-[#8F7858] border-l border-[#e4d9c6] pl-3">
              Reservations Console
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-[#2b2b2b]">{user?.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="border-[#c4b4a0] text-[#8F7858] rounded-full"
              data-testid="admin-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              size="sm"
              onClick={logout}
              className="bg-[#A48352] hover:bg-[#8F7858] text-white rounded-full"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-serif text-3xl text-[#1E1A14] mb-6">Enquiry Overview</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {TABS.map((t) => (
            <StatCard key={t.key} icon={t.icon} label={t.label} value={stats?.[t.key]} />
          ))}
        </div>

        <Tabs value={active} onValueChange={setActive}>
          <TabsList className="bg-white border border-[#e4d9c6] rounded-full p-1 flex-wrap h-auto">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className="rounded-full data-[state=active]:bg-[#A48352] data-[state=active]:text-white text-[#8F7858] px-4"
                data-testid={`admin-tab-${t.key}`}
              >
                {t.label}
                <Badge className="ml-2 bg-[#f4ead6] text-[#8F7858] hover:bg-[#f4ead6]">
                  {data[t.key]?.length ?? 0}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-6">
              {loading ? (
                <div className="text-center py-16 text-[#8F7858]">Loading…</div>
              ) : (
                <DataTable tabKey={t.key} rows={data[t.key] || []} />
              )}
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}
